import { useMininumDeviceWidthBreakpoint } from "@/hooks/media";
import { useUser } from "@/hooks/user";
import type { UsePaymentsResult } from "@/lib/backend/payments";
import { hadPremiumSubscription } from "@/lib/backend/utils";
import { formatCurrency } from "@/lib/utils";
import { m } from "@/paraglide/messages";
import { ButtonLink } from "@bidbattles/bloks/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@bidbattles/bloks/components/ui/dialog";
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerHandle,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger,
} from "@bidbattles/bloks/components/ui/drawer";
import { Link } from "@bidbattles/bloks/components/ui/link";
import { PageLoader } from "@bidbattles/bloks/components/ui/loading";
import {
	BBBackendClient,
	BBBackendQueryOptions,
} from "@bidbattles/bloks/lib/rapace";
import { Result } from "@bidbattles/bloks/lib/result";
import {
	QueryErrorResetBoundary,
	useMutation,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { Suspense, useState } from "react";
import { toast } from "sonner";
import { MolliePaymentForm } from "./payments.form";

export function PremiumUpsellModal({
	trigger,
	payments,
}: React.PropsWithChildren<{
	trigger: React.ReactElement<Record<string, unknown>>;
	// Pulled this up because the logic has to outlive the lifetime
	// of the modal being part of the React tree
	payments: UsePaymentsResult;
}>) {
	const user = useUser();
	const hasSubscribedBefore = user && hadPremiumSubscription(user.subscription);

	const { data: subscriptionTiers } = useSuspenseQuery(
		BBBackendQueryOptions.listSubscriptionTiers(),
	);
	const trialTier = hasSubscribedBefore
		? undefined
		: subscriptionTiers.find((tier) => tier.name === "TRIAL");
	const premiumTier = subscriptionTiers.find((tier) => tier.name === "PREMIUM");

	const [open, setOpen] = useState(false);
	const isAtLeastMedium = useMininumDeviceWidthBreakpoint({
		breakpoint: "md",
		serverDefault: true,
	});

	const upgradeMutation = useMutation({
		mutationKey: BBBackendQueryOptions.me().queryKey,
		mutationFn: async (params: { card_token: string }) => {
			const result = await payments.initiate(async ({ redirect_url }) => {
				const tier = trialTier ?? premiumTier;
				if (!tier) {
					return Result.error(new Error("An internal error occurred"));
				}

				return await BBBackendClient.createCheckoutSession({
					// NOTE: removed card token
					// ...params,
					item_type: "subscription_purchase",
					subscription_tier_id: tier.id,
					redirect_url,
				});
			});
			if (!result.ok) {
				toast.error(m.error_toast({ message: result.error.message }));
				return;
			}

			toast.success("Welcome to the Premium members club!");
			setOpen(false);
		},
	});

	const price = premiumTier?.price ?? 0;

	const upgradeCta = trialTier
		? `🚀 Upgrade now, at ${formatCurrency("EUR", trialTier.price, { minimumFractionDigits: 0 })} for your first month`
		: `🚀 Upgrade now, for ${formatCurrency("EUR", price, { minimumFractionDigits: 0 })}`;

	const content = (
		<>
			<ul className="flex flex-col gap-2.5 list-disc ms-4">
				<li>Convert all left over bids after a battle to loyalty coins</li>
				<li>Earn more from referrals</li>
				<li>And more future perks...</li>
			</ul>
			<QueryErrorResetBoundary>
				<Suspense fallback={<PageLoader />}>
					<MolliePaymentForm
						cancellable={false}
						submitLabel={upgradeCta}
						onSubmitSuccess={async (params) => {
							await upgradeMutation.mutateAsync({ card_token: params.token });
						}}
					/>
				</Suspense>
			</QueryErrorResetBoundary>
		</>
	);
	const title = "💎 BidBattles Premium";
	const description = (
		<>
			For the low price of {formatCurrency("EUR", price)}/month •{" "}
			<Link
				to="/account-types"
				className="text-accent-primary focus-visible:outline-accent-primary"
				onClick={() => setOpen(false)}
			>
				Learn more about Premium
			</Link>
		</>
	);

	return (
		<>
			<Dialog
				open={open && isAtLeastMedium}
				onOpenChange={(open) => {
					isAtLeastMedium && setOpen(open);
				}}
			>
				<DialogTrigger className="hidden md:inline-flex" render={trigger} />
				<DialogContent className="flex flex-col gap-4">
					<DialogHeader>
						<DialogTitle>{title}</DialogTitle>
						<DialogDescription>{description}</DialogDescription>
					</DialogHeader>
					{content}
				</DialogContent>
			</Dialog>
			<Drawer
				open={open && !isAtLeastMedium}
				onOpenChange={(open) => {
					!isAtLeastMedium && setOpen(open);
				}}
			>
				<DrawerTrigger asChild className="md:hidden">
					{trigger}
				</DrawerTrigger>
				<DrawerContent handle={<DrawerHandle />}>
					<DrawerHeader className="mb-4">
						<DrawerTitle>{title}</DrawerTitle>
						<DrawerDescription>{description}</DrawerDescription>
					</DrawerHeader>
					<div className="flex flex-col gap-4 items-stretch">{content}</div>
				</DrawerContent>
			</Drawer>
		</>
	);
}
